import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, peso, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";

const STATUS = ["pending", "paid", "preparing", "assigned", "out_for_delivery", "delivered", "cancelled"];
const STATUS_COLORS = {
  pending: "bg-[#F39C12] text-white",
  paid: "bg-[#2F5233] text-white",
  preparing: "bg-[#3498DB] text-white",
  assigned: "bg-[#8E44AD] text-white",
  out_for_delivery: "bg-[#E67E22] text-white",
  delivered: "bg-[#2F5233] text-white",
  cancelled: "bg-[#E74C3C] text-white",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [view, setView] = useState(null);

  const load = () => api.get("/orders").then((r) => setOrders(r.data));
  useEffect(() => {
    load();
    api.get("/drivers").then((r) => setDrivers(r.data));
  }, []);

  const assign = async (orderId, driverId) => {
    try { await api.post(`/orders/${orderId}/assign-driver`, { driver_id: driverId }); toast.success("Driver assigned"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const setStatus = async (orderId, status) => {
    try { await api.post(`/orders/${orderId}/status`, { status }); toast.success("Status updated"); load(); if (view?.id === orderId) setView({ ...view, status }); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#1C2826]">All orders</h2>
      <div className="bg-white border border-[#E1E5DF] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-[#F9F9F7] text-left text-xs uppercase tracking-wider text-[#4A5D5A]">
            <tr><th className="p-3">Order</th><th className="p-3">Customer</th><th className="p-3">Type</th><th className="p-3">Status</th><th className="p-3">Driver</th><th className="p-3">Total</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-[#E1E5DF]">
            {orders.map((o) => (
              <tr key={o.id} data-testid={`order-row-${o.id}`}>
                <td className="p-3">
                  <div className="font-semibold">{o.number}</div>
                  <div className="text-xs text-[#738380]">{new Date(o.created_at).toLocaleString("en-PH")}</div>
                </td>
                <td className="p-3">
                  <div>{o.customer_name}</div>
                  <div className="text-xs text-[#738380]">{o.customer_phone}</div>
                </td>
                <td className="p-3"><Badge variant="outline">{o.order_type}</Badge></td>
                <td className="p-3"><Badge className={STATUS_COLORS[o.status]}>{o.status.replace(/_/g, " ")}</Badge></td>
                <td className="p-3">
                  {o.order_type === "online" ? (
                    <Select value={o.driver_id || ""} onValueChange={(v) => assign(o.id, v)}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid={`assign-${o.id}`}><SelectValue placeholder="Assign..." /></SelectTrigger>
                      <SelectContent>
                        {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <span className="text-xs text-[#738380]">—</span>}
                </td>
                <td className="p-3 font-semibold">{peso(o.total)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => setView(o)} data-testid={`view-${o.id}`}>View</Button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-[#738380]">No orders</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!view} onOpenChange={(v) => !v && setView(null)}>
        <DialogContent className="max-w-2xl">
          {view && (
            <>
              <DialogHeader>
                <DialogTitle>Order {view.number}</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><div className="overline">Customer</div><div className="font-semibold">{view.customer_name}</div><div>{view.customer_phone}</div></div>
                  <div><div className="overline">Delivery</div><div>{view.customer_address}</div></div>
                </div>
                <div><div className="overline">Payment</div><div className="font-semibold capitalize">{view.payment_method}</div></div>
                {view.payment_proof && <img src={view.payment_proof} alt="proof" className="rounded-lg border border-[#E1E5DF] max-h-56" />}
                <div>
                  <div className="overline mb-1">Items</div>
                  <div className="space-y-1">
                    {view.items.map((it, idx) => (
                      <div key={`${it.product_id || it.name}-${it.variant_id || ""}-${idx}`} className="flex justify-between border-b border-[#E1E5DF] py-1.5">
                        <span>{it.quantity}× {it.name}</span>
                        <span className="font-semibold">{peso(it.price * it.quantity)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between border-t pt-2 font-bold">
                  <span>Total</span><span className="text-[#2F5233]">{peso(view.total)}</span>
                </div>
                <div>
                  <div className="overline mb-1">Update status</div>
                  <div className="flex flex-wrap gap-1.5">
                    {STATUS.map((s) => (
                      <Button key={s} size="sm" variant={view.status === s ? "default" : "outline"}
                        className={view.status === s ? "bg-[#2F5233] hover:bg-[#1F3D24] text-white" : ""}
                        onClick={() => setStatus(view.id, s)} data-testid={`status-${s}`}>
                        {s.replace(/_/g, " ")}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
